import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { usePrefersReducedMotion } from '../../hooks/useResponsive'

interface Particle {
  id: number
  x: number
  y: number
  size: number
  duration: number
  delay: number
  opacity: number
}

interface FloatingParticlesProps {
  count?: number
  className?: string
  particleColor?: string
  minSize?: number
  maxSize?: number
  minDuration?: number
  maxDuration?: number
}

export function FloatingParticles({
  count = 20,
  className = '',
  particleColor = 'rgba(59, 130, 246, 0.1)', // primary-500 with low opacity
  minSize = 4,
  maxSize = 12,
  minDuration = 15,
  maxDuration = 25,
}: FloatingParticlesProps) {
  const prefersReducedMotion = usePrefersReducedMotion()

  const particles = useMemo<Particle[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * (maxSize - minSize) + minSize,
      duration: Math.random() * (maxDuration - minDuration) + minDuration,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.3 + 0.1,
    }))
  }, [count, minSize, maxSize, minDuration, maxDuration])

  if (prefersReducedMotion) {
    return null
  }

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particleColor,
            opacity: particle.opacity,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            scale: [1, 1.2, 1],
            opacity: [particle.opacity, particle.opacity * 0.5, particle.opacity],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  )
}

// Gradient orbs for hero background
interface GradientOrbsProps {
  className?: string
}

export function GradientOrbs({ className = '' }: GradientOrbsProps) {
  const prefersReducedMotion = usePrefersReducedMotion()

  const orbs = useMemo(() => [
    {
      id: 1,
      size: 'w-72 h-72',
      position: 'top-0 left-0',
      gradient: 'bg-gradient-to-br from-primary-400/20 to-indigo-600/20',
      duration: 20,
    },
    {
      id: 2,
      size: 'w-96 h-96',
      position: 'top-1/4 right-0',
      gradient: 'bg-gradient-to-bl from-trade-400/15 to-primary-500/15',
      duration: 25,
    },
    {
      id: 3,
      size: 'w-80 h-80',
      position: 'bottom-0 left-1/4',
      gradient: 'bg-gradient-to-tr from-hr-400/20 to-intelligence-500/20',
      duration: 30,
    },
  ], [])

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {orbs.map((orb) => (
        <motion.div
          key={orb.id}
          className={`absolute rounded-full blur-3xl ${orb.size} ${orb.position} ${orb.gradient}`}
          animate={
            prefersReducedMotion
              ? {}
              : {
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                  rotate: [0, 180, 360],
                }
          }
          transition={
            prefersReducedMotion
              ? {}
              : {
                  duration: orb.duration,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
          }
        />
      ))}
    </div>
  )
}